import { proxy, ref } from 'valtio';
import { derive, proxyMap } from 'valtio/utils';

import { AtlassianClient } from '@canvas/atlassian-client';
import { FailureReasons, hostNotifier } from '@canvas/host-notifier';
import { safeGet, safeSet } from '@canvas/idb-cache';

import {
  DEVELOPMENT_ORIGIN,
  DEVELOPMENT_TENANTLESS_HOSTNAMES,
} from './site-service-constants';
import {
  getAvailableSitesUsedByCanvas,
  type ProductName,
  type Site,
  type AccessibleProductsRequest,
  PermissionId,
  type AccessibleProductsResponse,
  type Workspace,
  type ProductId,
} from './site-types';
import { toProductName } from './transformers/site-transformers';

const ACCESSIBLE_PRODUCTS_CACHE_KEY = 'canvas-accessible-products';
const ONE_HOUR_MS = 60 * 60 * 1000;

type CachedValue<ResponseType> = {
  response: ResponseType;
  time: number;
};

type WorkspaceWithProductId = Workspace & { productId: ProductId };

async function getCache<ResponseType>(key: string) {
  const cached = await safeGet<CachedValue<ResponseType>>(key);
  // ignore cached result if it is over an hour old
  if (!cached || cached?.time < Date.now() - ONE_HOUR_MS) {
    return undefined;
  }
  return cached.response;
}

const getAccessibleProductsCached = async () =>
  await getCache<AccessibleProductsResponse>(ACCESSIBLE_PRODUCTS_CACHE_KEY);

async function setAccessibleProductsCached(response: AccessibleProductsResponse) {
  await safeSet<CachedValue<AccessibleProductsResponse>>(ACCESSIBLE_PRODUCTS_CACHE_KEY, {
    response,
    time: Date.now(),
  });
}

export class SiteService {
  public initialized = false;
  public state: { jiraSites: Site[]; site: Site };

  private readonly observableSites = proxyMap<string, Site[]>();
  private readonly sites = new Map<string, Site[]>();
  private loadingPromise: Promise<AccessibleProductsResponse | undefined> | undefined;

  // don't be alarmed: silenceErrors will be set to false forever
  // once a non-preload call to loadSites() is made
  private silenceErrors = true;

  constructor(
    private readonly baseUrl: URL = new URL('/gateway/api', globalThis.location.origin),
    private readonly client = new AtlassianClient(
      globalThis.fetch.bind(globalThis),
      baseUrl
    )
  ) {
    const self = this;

    const sites = derive({
      jiraSites: (get) =>
        [...get(self.observableSites).values()]
          .flatMap((sites) => sites.filter((site) => site.products.includes('jira')))
          .sort((a, z) => a.name.localeCompare(z.name)),
    });

    this.state = derive(
      {
        jiraSites: (get) => get(sites).jiraSites,
        site: (get) => {
          const jiraSites = get(sites).jiraSites;

          const firstJiraSite = jiraSites[0];
          const currentJiraSite = self.getJiraSiteForOrigin(this.baseUrl.origin);

          const currentSite = self.getSiteByOriginWithSites(
            this.baseUrl.origin,
            self.getAllSites()
          );

          const site = currentJiraSite ?? firstJiraSite ?? currentSite;
          return site
            ? proxy({
                name: site.name,
                cloudId: site.cloudId,
                products: site.products,
                url: site.url ? ref(site.url) : site.url,
                iconUrl: site.iconUrl ? ref(site.iconUrl) : site.iconUrl,
              })
            : site;
        },
      },
      { sync: true }
    );
  }

  /**
   * Loads a list of sites accessible to the current user via the /accessible-products endpoint
   * This is cached and does not request again if sites are already loaded in memory.
   *
   * @param preload boolean, if preload is true, then this operation is treated
   * as a performance optimisation and errors will not be thrown.
   * Only use preload = true if you don't yet need to use sites data.
   * Use preload = false if you need to use the data
   * immediately after the returned promise is resolved.
   *
   * @returns void promise that resolved once sites are loaded
   */
  public async loadSites(preload = false) {
    // un-silence errors as soon as we have a non-preload call
    if (!preload) {
      this.silenceErrors = false;
    }

    // if sites are in memory, don't request again
    if (this.hasSites()) {
      return;
    }

    // if already loading data, await that and resolve this promise after it's complete
    if (this.loadingPromise) {
      await this.loadingPromise;
      return;
    }

    // load the data from an async source, and track this promise in .loadingPromise
    this.loadingPromise = this.loadSitesFromAsyncSource();
    await this.loadingPromise;
    this.loadingPromise = undefined;
  }

  private async loadSitesFromAsyncSource(): Promise<
    AccessibleProductsResponse | undefined
  > {
    // if sites are in local storage, load from local storage
    const cachedResponse = await getAccessibleProductsCached();
    if (cachedResponse) {
      this.updateSites(cachedResponse);
      return;
    }

    // if not, make request to get accessible products
    const response = await this.loadSitesFromAccessibleProducts();
    if (response) {
      // update in memory sites data
      this.updateSites(response);
      // and set localstorage cache for next time
      await setAccessibleProductsCached(response);
    }
  }

  private async loadSitesFromAccessibleProducts(): Promise<
    AccessibleProductsResponse | undefined
  > {
    const fullUrl = this.baseUrl.href + '/accessible-products';

    try {
      if (!this.silenceErrors) {
        hostNotifier.reportExternalCheckpoint({
          source: FailureReasons.PRODUCT_API_FAILURE,
          location: 'site-service',
          endpoint: fullUrl,
          dependencyId: 'accessible-products',
        });
      }

      const response = await this.client.post<
        AccessibleProductsRequest,
        AccessibleProductsResponse
      >('/accessible-products', {
        productIds: getAvailableSitesUsedByCanvas(),
        permissionIds: [PermissionId.WRITE],
      });

      // if no products, we do not want to throw according to unit tests
      // a re-request will occur next time this.loadSites() is called
      if (!response.products) {
        return;
      }

      // from this point on we assume the data returned from /accessible-products
      // is complete and correct. If we need more validation on returned data
      // it should be placed before this return, not in .updateSites()
      return response;
    } catch (e) {
      // if an error occurs while silenced (i.e. during preload), do nothing
      if (this.silenceErrors) {
        return;
      }

      // or else, report an external failure
      hostNotifier.reportExternalFailure({
        source: FailureReasons.PRODUCT_API_FAILURE,
        location: 'site-service',
        endpoint: fullUrl,
        dependencyId: 'accessible-products',
      });
      throw e;
    }
  }

  private updateSites(response: AccessibleProductsResponse) {
    this.sites.clear();
    const cloudIdToWorkspace = new Map<string, WorkspaceWithProductId[]>();
    let isCustomDomain = false;
    let orgId: string | null = null;

    // Transforms the result from accessible-products from a product to workspace map, to a cloudId to workspace map
    // Flatten the response to only the augmented the workspaces
    const workspaces = response.products.flatMap((product) => {
      return product.workspaces
        ? product.workspaces.map((workspace) => {
            return { ...workspace, productId: product.productId };
          })
        : [];
    });
    for (const workspace of workspaces) {
      const workspaceCloudId = workspace.cloudId;
      const workspaceUrlOrigin = new URL(workspace.workspaceUrl).origin;
      if (workspaceUrlOrigin === this.baseUrl.origin) {
        orgId = workspace.orgId;
        if (workspace.cloudUrl !== workspace.workspaceUrl) {
          isCustomDomain = true;
        }
      }
      const workspacesForCloudId = cloudIdToWorkspace.get(workspaceCloudId) ?? [];
      cloudIdToWorkspace.set(workspaceCloudId, workspacesForCloudId);
      workspacesForCloudId.push(workspace);
    }

    for (const cloudIdWorkspaces of cloudIdToWorkspace.values()) {
      // Splits the workspaces for a single cloudId by workspaceUrls. Different workspaces (e.g. different products)
      // can have the default workspace URL (the Atlassian site name) or a custom domain.
      const workspaceUrlToWorkspace = new Map<string, WorkspaceWithProductId[]>();
      for (const workspace of cloudIdWorkspaces) {
        const workspaceUrlOrigin = new URL(workspace.workspaceUrl).origin;
        const workspaceArray = workspaceUrlToWorkspace.get(workspaceUrlOrigin) ?? [];
        workspaceUrlToWorkspace.set(workspaceUrlOrigin, workspaceArray);
        workspaceArray.push(workspace);
      }
      for (const urlWorkspaces of workspaceUrlToWorkspace.values()) {
        // Removes any workspaces if we're on a custom domain, and the workspace orgId doesn't match the orgId of the
        // current site. If we're not a custom domain, everything is allowed.
        const filteredWorkspaces = urlWorkspaces.filter(
          (workspace) => !isCustomDomain || workspace.orgId === orgId
        );
        if (filteredWorkspaces.length === 0) {
          continue;
        }
        // If a workspaces have the same cloudId and the same workspaceURL, the display name and avatar will be the
        // same
        const firstWorkspace = filteredWorkspaces[0];
        const products = new Set(
          filteredWorkspaces
            .map((workspace) => toProductName(workspace.productId))
            .filter((productName): productName is ProductName => !!productName)
        );
        const site: Site = {
          name: firstWorkspace.workspaceDisplayName,
          url: ref(new URL(firstWorkspace.workspaceUrl)),
          products: Array.from(products).sort(),
          cloudId: firstWorkspace.cloudId,
          iconUrl: firstWorkspace.workspaceAvatarUrl
            ? ref(new URL(firstWorkspace.workspaceAvatarUrl))
            : null,
        };
        this.setSite(site);
      }
    }
    this.initialized = true;
  }

  private setSite(site: Site) {
    const cloudId = site.cloudId;
    const siteArray = this.sites.get(cloudId) ?? [];
    siteArray.push(site);
    this.observableSites.set(site.cloudId, siteArray);
    this.sites.set(site.cloudId, siteArray);
  }

  public hasSites() {
    return this.sites.size > 0;
  }

  public getSites(productName: ProductName) {
    return Array.from(this.sites.values()).flatMap((sites) =>
      sites
        .filter((site) => new Set(site.products).has(productName))
        .sort((a, z) => a.name.localeCompare(z.name))
    );
  }

  public getAllSites() {
    return Array.from(this.sites.values())
      .flat()
      .sort((a, z) => a.name.localeCompare(z.name));
  }

  public getSitesByCloudId(cloudId: string) {
    return this.sites.get(cloudId) ?? [];
  }

  public getSiteByCloudIdAndProduct(cloudId: string, productName: ProductName) {
    return this.getSitesByCloudId(cloudId).find((s) => s.products.includes(productName));
  }

  public getConfluenceSiteForOrigin(origin: string): Site | undefined {
    return this.getSiteByOriginAndProduct(origin, 'confluence');
  }

  public getJiraSiteForOrigin(origin: string): Site | undefined {
    return this.getSiteByOriginAndProduct(origin, 'jira');
  }

  private getSiteByOriginAndProduct(
    origin: string,
    productName: ProductName
  ): Site | undefined {
    const currentSite = this.getSiteByOriginWithSites(origin, this.getAllSites());
    if (!currentSite) {
      return undefined;
    }
    return this.getSiteByCloudIdAndProduct(currentSite.cloudId, productName);
  }

  private getSiteByOriginWithSites(origin: string, sites: Site[]): Site | undefined {
    let hostname = new URL(origin).hostname;

    const isTenantlessDevelopment = DEVELOPMENT_TENANTLESS_HOSTNAMES.includes(hostname);
    if (isTenantlessDevelopment) {
      hostname = DEVELOPMENT_ORIGIN.hostname;
    }
    return sites.find((s) => s.url.hostname === hostname);
  }

  /**
   * Site service is used as a singleton
   */
  private static instance: SiteService | null = null;

  static getInstance = (): SiteService => {
    if (!SiteService.instance) {
      SiteService.instance = new SiteService();
    }

    return SiteService.instance;
  };
}

export { type Site } from './site-types';
